import { flatten, isEmpty } from 'ramda';

import { ValidationErrorMessage } from '../../../components/file-upload-dialog/file-upload-dialog';
import {
  ValidatedFilename,
  ValidatedFilenameErrorCode,
} from '../../../graphql/generated';
import { EnhancedFile } from '../../../utils/file/file';
import {
  buildInvalidCharErrors,
  buildLengthErrors,
  buildUnsupportedFilenameErrors,
  createTupleOfValidInvalidFiles,
  transformFilesUsingFileValidationResult,
} from './validation-utils';

type ValidateFilesParams = {
  files: EnhancedFile[];
  validatedFilenames: ValidatedFilename[];
};

export type FileValidationResult = {
  hasValidationErrors: boolean;
  validatedFiles: EnhancedFile[];
  errors?: Record<ValidatedFilenameErrorCode, ValidationErrorMessage[]>;
};

export const transformFiles = async ({
  files,
  validatedFilenames,
}: ValidateFilesParams): Promise<FileValidationResult> => {
  const [validFiles, invalidFiles] = createTupleOfValidInvalidFiles(
    validatedFilenames
  );

  const hasValidationErrors = !isEmpty(flatten(invalidFiles));
  const filesWithLengthError = invalidFiles[0];
  const filesWithInvalidChars = invalidFiles[1];
  const filesWithUnsupportedFilename = invalidFiles[2];

  if (hasValidationErrors) {
    const transformedFiles = transformFilesUsingFileValidationResult(files, {
      validFiles,
      invalidFileConfig: {
        [ValidatedFilenameErrorCode.FILENAME_LENGTH_LIMIT]: filesWithLengthError,
        [ValidatedFilenameErrorCode.INVALID_CHARACTERS]: filesWithInvalidChars,
        [ValidatedFilenameErrorCode.UNSUPPORTED_FILE_TYPE]: filesWithUnsupportedFilename,
      },
    });
    
    const invalidCharsErrors = buildInvalidCharErrors(filesWithInvalidChars);
    const filenameLengthErrors = buildLengthErrors(filesWithLengthError);
    const unsupportedFilenameErrors = buildUnsupportedFilenameErrors(
      filesWithUnsupportedFilename
    );

    return {
      hasValidationErrors: true,
      validatedFiles: transformedFiles,
      errors: {
        [ValidatedFilenameErrorCode.INVALID_CHARACTERS]: invalidCharsErrors,
        [ValidatedFilenameErrorCode.FILENAME_LENGTH_LIMIT]: filenameLengthErrors,
        [ValidatedFilenameErrorCode.UNSUPPORTED_FILE_TYPE]: unsupportedFilenameErrors,
      },
    };
  }

  return { hasValidationErrors: false, validatedFiles: files };
};
